import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Channel } from '@src/api/shared/community/channelApiClient';
import SvgIconChatBalloon from '@src/assets/images/common/icon/channel/SvgIconChatBalloon';
import {
  ChannelVisibility,
  ChannelVisibilityEnum,
} from '@src/features/shared/community/enums/channelVisibility';
import {
  EmailNotification,
  EmailNotificationDisplayEnum,
} from '@src/features/shared/community/enums/emailNotification';
import {
  FileAttachment,
  FileAttachmentDisplayEnum,
} from '@src/features/shared/community/enums/fileAttachment';
import {
  PostPermission,
  PostPermissionDisplayEnum,
} from '@src/features/shared/community/enums/postPermission';
import DragIcon from '@src/ui/elements/table/DragIcon';
import MoreVertMenu from '@src/ui/elements/table/MoreVertMenu';
import { Emoji } from 'emoji-picker-react';
import React from 'react';

interface SortableChannelItemProps {
  channel: Channel;
  onMembersClick: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
  tableColumnStyles: Record<string, string>;
  order: number;
}

const SortableChannelItem: React.FC<SortableChannelItemProps> = ({
  channel,
  onMembersClick,
  onEditClick,
  onDeleteClick,
  tableColumnStyles,
  order,
}) => {
  const {
    isDragging,
    setActivatorNodeRef,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: channel.code,
  });

  return (
    <div
      ref={setNodeRef}
      className={`relative transition-colors [&:nth-child(2n)]:bg-gray-10 ${isDragging ? 'z-10 !bg-primary-50' : ''}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className='flex h-[60px] items-center text-sm'>
        <div
          ref={setActivatorNodeRef}
          className={`${tableColumnStyles.drag} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          {...attributes}
          {...listeners}
        >
          <DragIcon />
        </div>

        <div className={`flex ${tableColumnStyles.order} items-center px-4`}>{order}</div>
        <div className={`flex ${tableColumnStyles.name} items-center gap-2 px-4`}>
          {channel.emoji ? (
            <Emoji
              unified={channel.emoji}
              size={16}
            />
          ) : (
            <SvgIconChatBalloon
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
              }}
            />
          )}
          <span
            className='truncate'
            title={channel.name}
          >
            {channel.name}
          </span>
        </div>
        <div className={`flex ${tableColumnStyles.visibility} items-center px-4`}>
          {ChannelVisibilityEnum[channel.visibility as ChannelVisibility]}
        </div>
        <div className={`flex ${tableColumnStyles.permission} items-center px-4`}>
          {PostPermissionDisplayEnum[Number(channel.post_permission) as PostPermission]}
        </div>
        <div className={`flex ${tableColumnStyles.fileAttachment} items-center px-4`}>
          {FileAttachmentDisplayEnum[Number(channel.allow_file_attachment) as FileAttachment]}
        </div>
        <div className={`flex ${tableColumnStyles.emailNotification} items-center px-4`}>
          {EmailNotificationDisplayEnum[Number(channel.email_notification) as EmailNotification]}
        </div>
        <div className={`flex ${tableColumnStyles.action} items-center justify-center py-[9px]`}>
          <MoreVertMenu
            menuItems={[
              {
                label: '参加者一覧',
                onClick: onMembersClick,
              },
              {
                label: '編集する',
                onClick: onEditClick,
              },
              {
                label: '削除する',
                onClick: onDeleteClick,
                variant: 'warning',
              },
            ]}
            menuWidth={140}
          />
        </div>
      </div>
    </div>
  );
};

export default SortableChannelItem;
